import React, { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { authService } from "../../../services";
import { useCompany } from "../../../contexts/CompanyContext";
import { ThemeProvider, useTheme } from "../../../contexts/ThemeContext";
import { ColorModeProvider, useColorMode } from "../../../contexts/ColorModeContext";
import { Icons } from "../../shared/Icons";
import { SidebarButton } from "./SidebarButton";
import { SidebarReportsGroup } from "./SidebarReportsGroup";
import { SidebarCadastrosGroup } from "./SidebarCadastrosGroup";
import { useToaster } from "../../shared";

const ThemedAdminPanel: React.FC = () => {
  const { company, isLoading: companyLoading } = useCompany();
  const { theme, setCompany: setThemeCompany } = useTheme();
  const { isDark, toggleColorMode } = useColorMode();
  const { showToast } = useToaster();
  const userData = authService.getUserData();
  const userType = userData?.userType;
  const isProfessional = userType === "professional";
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Theme colors
  const primaryColor = theme.neutral[500];
  const primaryColorHover = theme.neutral[500];
  const sidebarCollapsed = isSidebarMinimized && !isMobileMenuOpen;

  useEffect(() => {
    if (company) {
      setThemeCompany(company); // Set theme based on company from context
    }
  }, [company, setThemeCompany]);

  const handleLogout = () => {
    authService.logout();
  };

  const getBasePath = () => {
    return "/admin";
  };

  const companyName = company?.name || "Loading...";

  // Get client URL
  const clientUrl = `${window.location.origin}/client`;

  const handleCopyClientUrl = async () => {
    try {
      await navigator.clipboard.writeText(clientUrl);
      showToast("success", "Link do cliente copiado com sucesso!");
    } catch (err) {
      console.error("Failed to copy URL:", err);
      // Fallback: select the text
      const textArea = document.createElement("textarea");
      textArea.value = clientUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      showToast("success", "URL copiada para a área de transferência!");
    }
  };

  // Show loading while company data is being loaded
  if (companyLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-slate-300">Carregando dados da empresa...</p>
        </div>
      </div>
    );
  }

  const companyLogo =
    company?.logo && company.logo !== "" ? company.logo : null;
  const companyInitial = (companyName.trim()[0] || "A").toUpperCase();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900">
      <div className="relative">
        {/* Mobile Overlay */}
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {!isMobileMenuOpen && (
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-xl shadow-md text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700"
            aria-label="Abrir menu"
          >
            <span className="text-xl leading-none">☰</span>
          </button>
        )}

        {/* Sidebar Navigation - fixed on desktop */}
        <nav
          className={`${
            sidebarCollapsed ? "w-16" : "w-64"
          } ${
            isMobileMenuOpen
              ? "fixed left-0 top-0 bottom-0 z-50 flex flex-col"
              : "hidden lg:flex lg:flex-col lg:fixed lg:left-0 lg:top-0 lg:bottom-0 lg:z-40"
          } bg-slate-100 dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 shadow-sm transition-all duration-300 overflow-visible`}
        >
          {/* Minimize/Maximize Button - Only on desktop; overflow-visible on nav so this isn't clipped */}
          <button
            onClick={() => setIsSidebarMinimized(!isSidebarMinimized)}
            className="hidden lg:flex absolute -right-3 top-6 z-[60] w-6 h-6 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-500 rounded-full shadow-md items-center justify-center text-gray-600 dark:text-slate-200 hover:text-gray-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-600 hover:shadow-lg transition-all duration-200"
            title={isSidebarMinimized ? "Expandir menu" : "Minimizar menu"}
          >
            {isSidebarMinimized ? (
              <Icons.ChevronRight sx={{ fontSize: 16 }} />
            ) : (
              <Icons.ChevronLeft sx={{ fontSize: 16 }} />
            )}
          </button>

          <div
            className={`${
              sidebarCollapsed ? "px-2 py-4" : "px-4 py-4 sm:px-5"
            } flex-shrink-0 border-b border-slate-200 dark:border-slate-700`}
          >
            <div
              className={`flex items-center ${
                sidebarCollapsed ? "justify-center" : "gap-3"
              }`}
            >
              {companyLogo ? (
                <img
                  src={companyLogo}
                  alt={`${companyName} logo`}
                  className={`flex-shrink-0 object-contain ${
                    sidebarCollapsed ? "h-9 w-9" : "h-10 w-auto max-w-[7rem]"
                  }`}
                />
              ) : (
                <div className="h-9 w-9 flex-shrink-0 rounded-lg bg-gray-800 dark:bg-slate-600 text-white flex items-center justify-center text-sm font-bold">
                  {companyInitial}
                </div>
              )}
              {!sidebarCollapsed && (
                <div className="min-w-0 flex-1">
                  <h1 className="text-base font-bold text-gray-900 dark:text-white truncate">
                    {companyName}
                  </h1>
                </div>
              )}
              {isMobileMenuOpen && (
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="lg:hidden p-1.5 text-gray-500 dark:text-slate-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg flex-shrink-0"
                  aria-label="Fechar menu"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          <div
            className={`${
              sidebarCollapsed ? "p-2" : "p-4 sm:px-5 sm:py-4"
            } transition-all duration-300 flex-1 overflow-y-auto`}
          >
            <div className="space-y-2">
              <SidebarButton
                icon={<Icons.Calendar sx={{ fontSize: 20 }} />}
                label="Agendamentos"
                path="/appointments"
                isMinimized={isSidebarMinimized && !isMobileMenuOpen}
                primaryColor={primaryColor}
                primaryColorHover={primaryColorHover}
                getBasePath={getBasePath}
                onNavigate={() => setIsMobileMenuOpen(false)}
              />

              {!isProfessional && (
                <SidebarButton
                  icon={<Icons.CalendarMonth sx={{ fontSize: 20 }} />}
                  label="Calendário"
                  path="/appointments-calendar"
                  isMinimized={isSidebarMinimized && !isMobileMenuOpen}
                  primaryColor={primaryColor}
                  primaryColorHover={primaryColorHover}
                  getBasePath={getBasePath}
                  onNavigate={() => setIsMobileMenuOpen(false)}
                />
              )}

              {userType === "company" && (
                <SidebarReportsGroup
                  isMinimized={isSidebarMinimized}
                  isMobileMenuOpen={isMobileMenuOpen}
                  primaryColor={primaryColor}
                  primaryColorHover={primaryColorHover}
                  getBasePath={getBasePath}
                  onNavigate={() => setIsMobileMenuOpen(false)}
                />
              )}

              {userType === "company" && company?.category === "car" && (
                <SidebarButton
                  icon={<Icons.Event sx={{ fontSize: 20 }} />}
                  label="Lembretes"
                  path="/week-reminders"
                  isMinimized={isSidebarMinimized && !isMobileMenuOpen}
                  primaryColor={primaryColor}
                  primaryColorHover={primaryColorHover}
                  getBasePath={getBasePath}
                  onNavigate={() => setIsMobileMenuOpen(false)}
                />
              )}

              {!isProfessional && (
                <>
                  {company?.category === "car" && (
                    <SidebarButton
                      icon={<Icons.Money sx={{ fontSize: 20 }} />}
                      label="Orçamentos"
                      path="/budgets"
                      isMinimized={isSidebarMinimized && !isMobileMenuOpen}
                      primaryColor={primaryColor}
                      primaryColorHover={primaryColorHover}
                      getBasePath={getBasePath}
                      onNavigate={() => setIsMobileMenuOpen(false)}
                    />
                  )}

                  <SidebarButton
                    icon={<Icons.Repeat sx={{ fontSize: 20 }} />}
                    label="Recorrências"
                    path="/recurrencies"
                    isMinimized={isSidebarMinimized && !isMobileMenuOpen}
                    primaryColor={primaryColor}
                    primaryColorHover={primaryColorHover}
                    getBasePath={getBasePath}
                    onNavigate={() => setIsMobileMenuOpen(false)}
                  />

                  <SidebarCadastrosGroup
                    isMinimized={isSidebarMinimized}
                    isMobileMenuOpen={isMobileMenuOpen}
                    primaryColor={primaryColor}
                    primaryColorHover={primaryColorHover}
                    getBasePath={getBasePath}
                    onNavigate={() => setIsMobileMenuOpen(false)}
                  />

                  <SidebarButton
                    icon={<Icons.Settings sx={{ fontSize: 20 }} />}
                    label="Configurações"
                    path="/settings"
                    isMinimized={isSidebarMinimized && !isMobileMenuOpen}
                    primaryColor={primaryColor}
                    primaryColorHover={primaryColorHover}
                    getBasePath={getBasePath}
                    onNavigate={() => setIsMobileMenuOpen(false)}
                  />
                </>
              )}
            </div>
          </div>

          <div
            className={`${
              sidebarCollapsed ? "p-2" : "p-4 sm:px-5"
            } flex-shrink-0 border-t border-slate-200 dark:border-slate-700 space-y-2`}
          >
            {userType === "company" && (
              <button
                onClick={handleCopyClientUrl}
                className={`${
                  sidebarCollapsed
                    ? "w-12 h-12 mx-auto flex items-center justify-center rounded-full"
                    : "w-full flex items-center gap-2 px-3 py-2 rounded-xl"
                } font-medium transition-all duration-200 text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-slate-800`}
                title="Copiar URL do cliente"
              >
                <Icons.ContentCopy sx={{ fontSize: 18 }} />
                {!sidebarCollapsed && <span>Cliente</span>}
              </button>
            )}
            <button
              className={`${
                sidebarCollapsed
                  ? "w-12 h-12 mx-auto flex items-center justify-center rounded-full"
                  : "w-full flex items-center gap-2 px-3 py-2 rounded-xl"
              } font-medium transition-all duration-200 text-sm text-gray-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-800`}
              onClick={toggleColorMode}
              title={isDark ? "Modo claro" : "Modo escuro"}
            >
              {isDark ? (
                <Icons.LightMode sx={{ fontSize: 18 }} />
              ) : (
                <Icons.DarkMode sx={{ fontSize: 18 }} />
              )}
              {!sidebarCollapsed && (
                <span>{isDark ? "Modo claro" : "Modo escuro"}</span>
              )}
            </button>
            <button
              className={`${
                sidebarCollapsed
                  ? "w-12 h-12 mx-auto flex items-center justify-center rounded-full"
                  : "w-full flex items-center gap-2 px-3 py-2 rounded-xl"
              } font-medium transition-all duration-200 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40`}
              onClick={handleLogout}
              title="Sair"
            >
              <Icons.Logout sx={{ fontSize: 18 }} />
              {!sidebarCollapsed && <span>Sair</span>}
            </button>
          </div>
        </nav>

        {/* Main Content - offset for fixed sidebar */}
        <main
          className={`min-h-screen px-4 pb-4 sm:px-6 sm:pb-6 md:px-8 md:pb-8 overflow-auto pt-16 lg:pt-8 ${
            isSidebarMinimized ? "lg:ml-16" : "lg:ml-64"
          }`}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
};

// Main component with ThemeProvider wrapper
export const AdminPanel: React.FC = () => {
  return (
    <ThemeProvider>
      <ColorModeProvider>
        <ThemedAdminPanel />
      </ColorModeProvider>
    </ThemeProvider>
  );
};
